import './help.css';
import { CHARACTERS, RIVALS } from '../data/roster';
import { ITEMS } from '../data/items';
import { TRACKS } from '../data/tracks';
import { GAMEPAD_HINT, LAYOUTS, LAYOUT_ORDER } from '../core/input';
import type { CharacterDef, ItemDef, ItemKind } from '../core/types';
import type { Overlay } from './controls';

type TabId = 'abilities' | 'items' | 'modes' | 'tracks' | 'controls';

const TABS: readonly { id: TabId; label: string }[] = [
  { id: 'abilities', label: 'KÉPESSÉGEK' },
  { id: 'items', label: 'EXTRÁK' },
  { id: 'modes', label: 'MÓDOK' },
  { id: 'tracks', label: 'PÁLYÁK' },
  { id: 'controls', label: 'VEZÉRLÉS' },
];

const STAT_ROWS: readonly { key: keyof CharacterDef['stats']; label: string }[] = [
  { key: 'topSpeed', label: 'végsebesség' },
  { key: 'accel', label: 'gyorsulás' },
  { key: 'handling', label: 'kormányzás' },
  { key: 'grip', label: 'tapadás' },
  { key: 'weight', label: 'tömeg' },
];

const ITEM_KIND: Record<ItemKind, string> = {
  self: 'önmagadra',
  projectile: 'előredobott',
  drop: 'hátrahagyott',
  global: 'mindenkire',
};

const THEME: Record<string, string> = {
  city: 'belváros',
  suburb: 'külváros',
  lake: 'tópart',
  circuit: 'nagydíjpálya',
  desert: 'sivatagi bánya',
};

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

const button = (className: string, text: string): HTMLButtonElement => {
  const node = el('button', className, text);
  node.type = 'button';
  return node;
};

const heading = (title: string, sub: string): HTMLElement => {
  const box = el('div', 'csf-help__section');
  box.append(el('div', 'csf-help__section-title', title), el('p', 'csf-help__section-sub', sub));
  return box;
};

const characterCard = (character: CharacterDef): HTMLElement => {
  const card = el('article', 'csf-help__card csf-help__char');
  card.style.setProperty('--char', character.colors.body);
  card.style.setProperty('--char-accent', character.colors.accent);

  const top = el('div', 'csf-help__char-top');
  const dot = el('span', 'csf-help__char-dot');
  const names = el('div', 'csf-help__char-names');
  names.append(
    el('h3', 'csf-help__char-name', character.name),
    el('div', 'csf-help__char-role', `${character.role} · ${character.vehicle.name}`),
  );
  const badge = el(
    'span',
    `csf-help__badge${character.selectable ? ' is-playable' : ''}`,
    character.selectable ? 'VÁLASZTHATÓ' : 'AI RIVÁLIS',
  );
  top.append(dot, names, badge);

  const ability = el('div', 'csf-help__ability');
  const abilityHead = el('div', 'csf-help__ability-head');
  abilityHead.append(
    el('span', 'csf-help__ability-label', 'KÉPESSÉG'),
    el('strong', 'csf-help__ability-name', character.ability.name),
    el(
      'span',
      'csf-help__ability-meta',
      `újratöltés ${character.ability.cooldown} mp · hatás ${character.ability.duration} mp`,
    ),
  );
  ability.append(abilityHead, el('p', 'csf-help__ability-desc', character.ability.desc));

  const stats = el('div', 'csf-help__stats');
  for (const row of STAT_ROWS) {
    const line = el('div', 'csf-help__stat');
    const label = el('span', 'csf-help__stat-label', row.label);
    const track = el('span', 'csf-help__stat-track');
    const fill = el('span', 'csf-help__stat-fill');
    fill.style.width = `${Math.max(4, Math.min(100, character.stats[row.key] * 10))}%`;
    track.append(fill);
    line.append(label, track);
    stats.append(line);
  }

  const bio = el('p', 'csf-help__bio', character.bio);

  card.append(top, ability, stats, bio);
  return card;
};

const itemCard = (item: ItemDef): HTMLElement => {
  const card = el('article', 'csf-help__card csf-help__item');
  card.style.setProperty('--item-color', item.color);

  const top = el('div', 'csf-help__item-top');
  const icon = el('span', 'csf-help__item-icon', item.icon);
  const names = el('div', 'csf-help__item-names');
  names.append(
    el('h3', 'csf-help__item-name', item.name),
    el('div', 'csf-help__item-kind', ITEM_KIND[item.kind]),
  );
  top.append(icon, names);
  if (item.comeBack) {
    top.append(el('span', 'csf-help__badge is-comeback', 'HÁTUL GYAKORIBB'));
  }

  card.append(top, el('p', 'csf-help__item-desc', item.blurb));
  return card;
};

const extraCard = (icon: string, name: string, desc: string): HTMLElement => {
  const card = el('article', 'csf-help__card csf-help__item csf-help__extra');
  const top = el('div', 'csf-help__item-top');
  const badge = el('span', 'csf-help__item-icon', icon);
  const names = el('div', 'csf-help__item-names');
  names.append(el('h3', 'csf-help__item-name', name), el('div', 'csf-help__item-kind', 'pályán található'));
  top.append(badge, names);
  card.append(top, el('p', 'csf-help__item-desc', desc));
  return card;
};

const modeCard = (name: string, tag: string, desc: string): HTMLElement => {
  const card = el('article', 'csf-help__card csf-help__mode');
  card.append(
    el('h3', 'csf-help__mode-name', name),
    el('span', 'csf-help__badge', tag),
    el('p', 'csf-help__mode-desc', desc),
  );
  return card;
};

export interface HelpOptions {
  onClose?: () => void;
}

/**
 * Súgó / ismertető: karakterképességek, tárgyak, módok, pályák és vezérlés.
 * A címképernyőről (SÚGÓ gomb vagy H) nyílik; Esc/bezárás gomb zárja.
 */
export function showHelp(root: HTMLElement, opts: HelpOptions = {}): Overlay {
  const wrap = el('div', 'csf-help');
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-modal', 'true');
  wrap.setAttribute('aria-labelledby', 'csf-help-title');
  wrap.tabIndex = -1;

  const panel = el('div', 'csf-help__panel');
  const head = el('div', 'csf-help__head');
  const headText = el('div', 'csf-help__head-text');
  const title = el('h2', 'csf-help__title', 'SÚGÓ');
  title.id = 'csf-help-title';
  headText.append(title, el('p', 'csf-help__sub', 'Képességek, extrák, pályák és vezérlés – egy helyen.'));
  const closeBtn = button('csf-help__close', '×');
  closeBtn.setAttribute('aria-label', 'Súgó bezárása');
  head.append(headText, closeBtn);

  const tabs = el('div', 'csf-help__tabs');
  tabs.setAttribute('role', 'tablist');
  const body = el('div', 'csf-help__body');
  body.id = 'csf-help-body';
  body.setAttribute('role', 'tabpanel');
  body.tabIndex = 0;

  const tabButtons = new Map<TabId, HTMLButtonElement>();
  for (const tab of TABS) {
    const tabButton = button('csf-help__tab', tab.label);
    tabButton.setAttribute('role', 'tab');
    tabButton.dataset.tab = tab.id;
    tabButtons.set(tab.id, tabButton);
    tabs.append(tabButton);
  }

  panel.append(head, tabs, body);
  wrap.append(panel);
  root.append(wrap);

  let active: TabId = 'abilities';
  let closed = false;

  const finish = (): void => {
    if (closed) return;
    closed = true;
    window.removeEventListener('keydown', onKey, true);
    wrap.remove();
    opts.onClose?.();
  };

  const setTab = (id: TabId): void => {
    active = id;
    for (const [tabId, tabButton] of tabButtons) {
      const selected = tabId === id;
      tabButton.classList.toggle('is-active', selected);
      tabButton.setAttribute('aria-selected', String(selected));
      tabButton.tabIndex = selected ? 0 : -1;
    }
    body.replaceChildren();
    body.scrollTop = 0;
    renderBody(body, id);
  };

  for (const [id, tabButton] of tabButtons) {
    tabButton.addEventListener('click', () => setTab(id));
  }

  const onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      finish();
      return;
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.stopPropagation();
      const current = TABS.findIndex((tab) => tab.id === active);
      const next = (current + (event.key === 'ArrowRight' ? 1 : -1) + TABS.length) % TABS.length;
      const tab = TABS[next];
      if (tab) setTab(tab.id);
    }
  };

  closeBtn.addEventListener('click', finish);
  wrap.addEventListener('pointerdown', (event) => {
    if (event.target === wrap) finish();
  });
  window.addEventListener('keydown', onKey, true);

  setTab(active);
  closeBtn.focus({ preventScroll: true });

  return { close: finish, element: wrap };
}

function renderBody(body: HTMLElement, id: TabId): void {
  switch (id) {
    case 'abilities': {
      body.append(
        heading(
          'KÉPESSÉGEK',
          'A 4 választható karakter képessége, statisztikái és járműve. A riválisok ugyanazokat a képességtípusokat használják.',
        ),
      );
      const playable = el('div', 'csf-help__grid');
      for (const character of CHARACTERS) playable.append(characterCard(character));
      body.append(playable);
      body.append(
        heading(
          'RIVÁLISOK (AI)',
          'Őket nem lehet választani, de ugyanazokkal a mechanikákkal versenyeznek – érdemes tudni, mire képesek.',
        ),
      );
      const rivals = el('div', 'csf-help__grid');
      for (const character of RIVALS) rivals.append(characterCard(character));
      body.append(rivals);
      break;
    }
    case 'items': {
      body.append(
        heading(
          'EXTRÁK',
          'A kérdőjeles ládákból véletlen tárgy kerül elő. Az élen állók inkább akadályozó, a hátul lévők inkább felzárkóztató tárgyat kapnak.',
        ),
      );
      const grid = el('div', 'csf-help__grid');
      for (const item of ITEMS) grid.append(itemCard(item));
      body.append(grid);
      body.append(
        heading(
          'PÁLYA-ELEMEK',
          'Ezek nem a ládákból jönnek, hanem a pályán találhatók.',
        ),
      );
      const extras = el('div', 'csf-help__grid');
      extras.append(
        extraCard('»', 'Boost-pad', 'Sárga nyíl a pályán: azonnali sebesség-löket, a legjobb íven a legtöbbet éri.'),
        extraCard('?', 'Tárgyláda', 'Kérdőjeles láda: véletlen tárgyat ad, majd egy idő után újra megjelenik.'),
        extraCard('◯', 'Kátyú', 'A sivatagi pálya aszfaltján: ha belehajtasz, kipördülsz vagy lelassulsz. Ugrással átugorható.'),
        extraCard('✳', 'Ördögszekér', 'A sivatagi pályán néha átfúj az úton; ha eltalál, kipördülsz. Az irányát figyeld!'),
      );
      body.append(extras);
      break;
    }
    case 'modes': {
      body.append(
        heading(
          'MÓDOK',
          'A helyi mód a pályakártyán, az online a címképernyő ONLINE gombjával indul.',
        ),
      );
      const grid = el('div', 'csf-help__grid');
      grid.append(
        modeCard(
          'Egyjátékos',
          '1 játékos',
          'Egy kormány mögött, a mezőnyt AI riválisok töltik fel. A gyakorlás egyedül, 3 körön át, AI nélkül.',
        ),
        modeCard(
          'Helyi 2 / 4 játékos',
          'osztott képernyő',
          'Ugyanazon a gépen, két vagy négy nézetben. A hiányzó helyeket AI töltheti be.',
        ),
        modeCard(
          'PvP',
          'AI nélkül',
          'Csak az emberi játékosok versenyeznek (2 vagy 4 kart), a többiek nem indulnak. Tiszta egymás elleni futam.',
        ),
        modeCard(
          'Battle 2v2',
          'csapatcsata',
          'Két csapat, 3-3 élet. Aki elfogy, kiesik; az utolsó csapat nyer. A pálya ugyanaz, a cél más.',
        ),
        modeCard(
          'Online',
          'szobakód',
          'A gazda 6 jegyű kóddal szobát nyit; max. 4 ember, a maradék helyeket AI tölti be. A gazda indít, közben már nem lehet belépni.',
        ),
        modeCard(
          'Gyakorlás',
          '1 kart',
          'Szabad körök a választott pályán, AI nélkül, a beállított körszámmal. Jó az ív, a drift és a képességek kipróbálására.',
        ),
      );
      body.append(grid);
      body.append(
        heading(
          'GYORS TIPPEK',
          'Pár dolog, ami sokat számít.',
        ),
      );
      const tips = el('ul', 'csf-help__tips');
      for (const tip of [
        'Kanyarban a drift + rövid gáz adja a legjobb kijáratot; a falnál a szegélykő lassít.',
        'A boost-padot a lehető legkisebb kanyarodással vedd be, hogy a löket egyenesen hasznosuljon.',
        'A tárgyat a képernyő jobb alsó sarkában látod; a képesség külön gomb, és van újratöltése.',
        'Az AI riválisok karaktere más-más: Robi agresszív, Béla nyugodt, Krisztina egyenesben gyors.',
      ]) {
        tips.append(el('li', 'csf-help__tip', tip));
      }
      body.append(tips);
      break;
    }
    case 'tracks': {
      body.append(
        heading(
          'PÁLYÁK',
          'Öt helyszín, közös fizikával. A körök száma a pályakártyán 1–10 között állítható (alapból 3); az éjszakai mód ugyanazt a pályát sötétíti el.',
        ),
      );
      const grid = el('div', 'csf-help__grid');
      for (const track of TRACKS) {
        const card = el('article', 'csf-help__card csf-help__track');
        const top = el('div', 'csf-help__track-top');
        top.append(
          el('h3', 'csf-help__track-name', track.name),
          el('span', 'csf-help__badge', THEME[track.theme ?? 'city'] ?? track.theme ?? ''),
        );
        card.append(
          top,
          el('div', 'csf-help__track-sub', track.subtitle),
          el('p', 'csf-help__track-desc', track.description),
          el('div', 'csf-help__track-meta', `${track.laps} kör alapból · 1–10 állítható · ${track.itemRows.length} tárgysor · ${track.boostPads.length} boost-pad`),
        );
        grid.append(card);
      }
      body.append(grid);
      break;
    }
    case 'controls': {
      body.append(
        heading(
          'VEZÉRLÉS',
          'A kiosztás a címképernyő BEÁLLÍTÁSOK paneljén vagy a pályakártyán választható; verseny közben Esc / II nyitja a szünetet.',
        ),
      );
      const keys = el('div', 'csf-help__keys');
      for (const layoutId of LAYOUT_ORDER) {
        const layout = LAYOUTS[layoutId];
        const row = el('div', 'csf-help__key-row');
        row.append(
          el('span', 'csf-help__key-name', layout.name),
          el('span', 'csf-help__key-desc', layout.keys),
        );
        keys.append(row);
      }
      body.append(keys);
      const other = el('div', 'csf-help__key-other');
      other.append(
        el('p', 'csf-help__key-desc', `Gamepad: ${GAMEPAD_HINT}`),
        el('p', 'csf-help__key-desc', 'A gamepad gombjai a beállításokban átmappelhetők: egy funkcióhoz több gomb is rendelhető, egy gomb viszont csak egy funkcióé.'),
        el('p', 'csf-help__key-desc', 'Telefon: bal hüvelykujjal kormánykarika, jobb oldalon GÁZ / FÉK / DRIFT / TÁRGY / KÉP., felül II (szünet) és ⛶ (teljes képernyő).'),
        el('p', 'csf-help__key-desc', 'A játék 60 Hz-en fut; az online módban a saját kocsi predikciója a szünetpanelen kapcsolható.'),
      );
      body.append(other);
      break;
    }
    default:
      break;
  }
}
